// src/pages/conversations/ConversationList.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress,
  IconButton, Link, Tooltip, Alert, TextField, InputAdornment,
  TablePagination, Stack, Snackbar
} from '@mui/material';
import {
  Visibility as ViewIcon, // Kept for explicit view action
  Search as SearchIcon,
  PersonOutline as AgentIcon,
  // Removed ConversationIcon from here as it's in ConversationListItemCell
} from '@mui/icons-material';
import { conversationService } from '../../api/conversationService';
import { toast } from 'react-toastify';
import { useDebounce } from '../../hooks/useDebounce';
import { getConversationPreview, formatDisplayDate } from '../../utils/conversationUtils';

// Import new components
import QuickPreviewModal from '../../components/conversations/QuickPreviewModal'; // Adjust path if needed
import ConversationListItemCell from './ConversationListItemCell'; // Assuming it's in the same folder

const ConversationList = ({ agentIdForFilter = null }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalConversations, setTotalConversations] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const sortBy = 'updated_at';
  const sortOrder = 'desc';

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // State for Quick Preview Modal
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedConvForPreview, setSelectedConvForPreview] = useState(null);

  useEffect(() => {
    if (location.state?.snackbar) {
      setSnackbar({ open: true, ...location.state.snackbar });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await conversationService.listConversations(
      agentIdForFilter, rowsPerPage, page * rowsPerPage,
      debouncedSearchTerm || null, sortBy, sortOrder
    );
    if (res.isError) {
      setError(res.message || 'Failed to fetch conversations.');
      setConversations([]); setTotalConversations(0);
      toast.error(res.message || 'Failed to fetch conversations.');
    } else {
      setConversations(Array.isArray(res.items) ? res.items : []);
      setTotalConversations(res.total || 0);
    }
    setLoading(false);
  }, [agentIdForFilter, page, rowsPerPage, debouncedSearchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const getCurrentListParams = () => ({
    page, rowsPerPage, searchTerm: debouncedSearchTerm,
    agentIdForFilter, sortBy, sortOrder, totalConversations
  });

  const handleViewConversation = (convId) => {
    navigate(`/conversations/${convId}`, { state: { listParams: getCurrentListParams() } });
  };
  
  const handleOpenQuickPreview = (conversation) => {
    setSelectedConvForPreview(conversation);
    setIsPreviewOpen(true);
  };
  const handleCloseQuickPreview = () => setIsPreviewOpen(false);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      {!agentIdForFilter && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
          <Typography variant="h4">All Conversations</Typography>
        </Stack>
      )}
      {agentIdForFilter && (
         <Typography variant="h6" sx={{fontWeight: 600, mb: 2.5}}>
            Conversation History
         </Typography>
      )}

      <Paper sx={{ mb: 2, p: 2, border: (theme) => `1px solid ${theme.palette.divider}`}} elevation={0}>
        <TextField fullWidth variant="outlined" size="small"
          placeholder="Search conversations (e.g., by content, ID)..."
          value={searchTerm} onChange={handleSearchChange}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
          }}
        />
      </Paper>

      {loading && conversations.length === 0 ? (
        <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
        <Paper sx={{border: (theme) => `1px solid ${theme.palette.divider}` }} elevation={0}>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="conversations table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: {xs: '50%', sm:'60%'} }}>Preview / First Message</TableCell>
                  {!agentIdForFilter && <TableCell sx={{ fontWeight: 'bold', width: {xs: '25%', sm:'15%'} }}>Agent</TableCell>}
                  <TableCell sx={{ fontWeight: 'bold', width: {xs: '25%', sm:'15%'} }}>Last Updated</TableCell>
                  {/* Created At can be moved to modal or kept if space allows */}
                  {/* <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell> */}
                  <TableCell align="right" sx={{ fontWeight: 'bold', width: '10%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conversations.length === 0 ? (
                   <TableRow>
                      <TableCell colSpan={agentIdForFilter ? 3 : 4} align="center" sx={{ py: 4 }}>
                        No conversations found matching your criteria.
                      </TableCell>
                   </TableRow>
                ) : (
                  conversations.map(conv => (
                    <TableRow 
                      key={conv.id} 
                      hover 
                      onClick={() => handleViewConversation(conv.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                      }}
                    >
                      <TableCell sx={{ py: 1, px: 1.5, maxWidth: 450 }}>
                        <ConversationListItemCell
                          conversation={conv}
                          onQuickPreviewClick={handleOpenQuickPreview}
                          listParamsForNavigation={getCurrentListParams()}
                        />
                      </TableCell>
                      {!agentIdForFilter && (
                        <TableCell sx={{ py: 1, px: 1.5 }}>
                          {conv.agent_name ? (
                            <Link 
                              component={RouterLink} 
                              to={`/agent-workspace/${conv.agent_id}/overview`} 
                              onClick={(e) => e.stopPropagation()} // Prevent row click
                              sx={{display:'flex', alignItems:'center', textDecoration: 'none'}}
                            >
                                <AgentIcon sx={{mr: 0.5, fontSize: '1.1rem', color: 'text.secondary'}}/> 
                                <Typography variant="body2" color="text.primary" noWrap>{conv.agent_name}</Typography>
                            </Link>
                          ) : ( conv.agent_id ? `ID: ...${conv.agent_id.slice(-6)}` : 'N/A')}
                        </TableCell>
                      )}
                      <TableCell sx={{ py: 1, px: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {formatDisplayDate(conv.updated_at)}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>{formatDisplayDate(conv.created_at)}</TableCell> */}
                      <TableCell align="right" sx={{ py: 1, px: 1.5 }}>
                         <Tooltip title="View Full Conversation">
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleViewConversation(conv.id);
                            }} 
                            size="small"
                            aria-label="View full conversation"
                          >
                            <ViewIcon />
                          </IconButton>
                         </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalConversations}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{borderTop: (theme) => `1px solid ${theme.palette.divider}`}}
            />
          </Paper>
        </>
      )}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      {selectedConvForPreview && (
        <QuickPreviewModal
          open={isPreviewOpen}
          onClose={handleCloseQuickPreview}
          conversation={selectedConvForPreview}
          onNavigateToListParams={getCurrentListParams()}
        />
      )}
    </Box>
  );
};

export default ConversationList;